import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// Clean, AI-SaaS styled card with Bento Grid Logic
const AIFeatureCard = ({ service, index, disableWow }) => {
  // Bento pattern: Wide, Square, Square, Wide...
  const isLarge = index % 4 === 0 || index % 4 === 3;
  const colClass = isLarge ? "col-xl-8 col-lg-7" : "col-xl-4 col-lg-5";

  return (
    <div className={`${colClass} col-md-6 ${disableWow ? "" : "wow fadeInUp"}`} data-wow-delay={service.delay || ".3s"}>
      <div className="ai-feature-card h-100 position-relative rounded-4 overflow-hidden shadow-premium glass-panel border border-white-10">
        
        {/* Animated Background Gradient */}
        <div className="position-absolute top-0 start-50 translate-middle-x w-100 h-100 z-0 pointer-events-none opacity-0 transition-opacity ai-card-glow" style={{ background: "radial-gradient(circle at center, rgba(99, 102, 241, 0.15) 0%, transparent 60%)" }}></div>

        <div className={`position-relative z-1 h-100 p-4 p-lg-5 d-flex ${isLarge ? "flex-column flex-md-row align-items-md-center gap-4" : "flex-column"}`}>
          
          <div className={`d-flex flex-column h-100 ${isLarge ? "flex-grow-1 pe-md-4" : ""}`}>
            <div className="icon-wrapper mb-4 d-inline-flex align-items-center justify-content-center rounded-3">
              {service.icon?.startsWith("fa-") ? (
                <i className={`${service.icon} text-white fs-4`}></i>
              ) : (
                <img src={service.icon} alt={service.title} style={{ width: "32px", height: "32px", filter: "brightness(0) invert(1)" }} />
              )}
            </div>
            
            <h3 className="h4 fw-bolder text-white mb-3" style={{ letterSpacing: "-0.02em" }}>
              {service.title}
            </h3>
            
            <p className="text-secondary mb-4 flex-grow-1" style={{ fontSize: "1.05rem", lineHeight: "1.6" }}>
              {service.description}
            </p>

            <Link to={service.link || "#"} className="ai-link fw-bold text-decoration-none d-inline-flex align-items-center mt-auto">
              <span className="text-white">Generate Now</span> 
              <i className="fa-solid fa-arrow-right ms-2 fs-6 opacity-75"></i>
            </Link>
          </div>

          {/* Decorative abstract visual for large bento cards */}
          {isLarge && (
             <div className="d-none d-md-flex align-items-center justify-content-center ms-auto position-relative" style={{ width: "200px", height: "200px" }}>
                <div className="w-100 h-100 rounded-circle border border-white-10 d-flex align-items-center justify-content-center position-relative overflow-hidden">
                   <div className="position-absolute w-100 h-100 rounded-circle spin-slow" style={{ border: "1px dashed rgba(255,255,255,0.15)" }}></div>
                   <div className="position-absolute w-75 h-75 rounded-circle spin-slow-reverse" style={{ border: "1px dashed rgba(99, 102, 241, 0.3)" }}></div>
                   <div className="icon-wrapper-large d-flex align-items-center justify-content-center rounded-circle z-1" style={{ width: "80px", height: "80px", background: "rgba(99, 102, 241, 0.1)", backdropFilter: "blur(4px)" }}>
                     {service.icon?.startsWith("fa-") ? (
                        <i className={`${service.icon} text-gradient`} style={{ fontSize: "36px" }}></i>
                      ) : (
                        <img src={service.icon} alt="" style={{ width: "40px", height: "40px", opacity: 0.9, filter: "brightness(0) invert(1)" }} />
                      )}
                   </div>
                </div>
             </div>
          )}

        </div>
      </div>
    </div>
  );
};

const FeaturesSection = ({ disableWow = false }) => {
  const [sectionData, setSectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchServicesData = async () => {
      try {
        const response = await fetch("https://allinone-clone-7d5d795e98ce.herokuapp.com/api/services-section-v3");
        const result = await response.json();

        if (result.success) {
          setSectionData(result.data);
        }
      } catch (error) {
        console.error("Error fetching services section v3:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchServicesData();
  }, []);

  if (loading) return null;
  if (!sectionData) return null;

  return (
    <>
      <section className="features-section position-relative overflow-hidden py-5">
        {/* Modern Dark Backgrounds with subtle ambient glows */}
        <div className="bg-absolute bg-dark-base"></div>
        <div className="ambient-glow glow-primary"></div>
        <div className="ambient-glow glow-secondary"></div>

        <div className="container-xl position-relative z-1 mt-5 pt-lg-5 pb-5 px-xl-5">
          
          {/* Centered Header Layout */}
          <div className="row justify-content-center text-center mb-5 pb-3">
            <div className={`col-lg-8 ${disableWow ? "" : "wow fadeInUp"}`} data-wow-duration="1s">
              <div className="badge-wrapper mb-4">
                <span className="badge-premium d-inline-flex align-items-center px-3 py-2 rounded-pill border border-white-10 glass-panel tracking-wider text-uppercase shadow-sm">
                  <span className="badge-dot me-2"></span>
                  <span className="text-secondary fw-semibold text-xs">{sectionData.subtitle || "Features"}</span>
                </span>
              </div>
              
              <h2 className="display-3 fw-bolder text-white mb-4 title-glow" style={{ letterSpacing: "-0.04em", lineHeight: "1.15" }}>
                {sectionData.titleLine1} <span style={{ color: "#818cf8" }}>{sectionData.titleLine2}</span>
              </h2>
              
              <p className="lead text-secondary mx-auto fs-5" style={{ maxWidth: "800px", lineHeight: "1.7" }}>
                {sectionData.description}
              </p>
            </div>
          </div>

          {/* Bento Grid Layout for Cards */}
          <div className="row g-4">
            {sectionData.services?.slice(0, showAll ? sectionData.services.length : 10).map((service, index) => (
              <AIFeatureCard key={service.id} service={service} index={index} disableWow={disableWow} />
            ))}
          </div>

          {/* See More / Show Less Button */}
          {sectionData.services?.length > 10 && (
            <div className="text-center mt-5 position-relative z-1">
              <button 
                onClick={() => setShowAll(!showAll)}
                className="btn text-white rounded-pill px-5 py-3 fw-bold shadow-premium glass-panel border border-white-10"
                style={{
                  transition: "all 0.3s ease",
                  letterSpacing: "0.5px",
                  background: "rgba(15, 23, 42, 0.6)"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(99, 102, 241, 0.2)";
                  e.currentTarget.style.borderColor = "rgba(99, 102, 241, 0.5)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(15, 23, 42, 0.6)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.08)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                {showAll ? (
                  <>Show Less Tools <i className="fa-solid fa-chevron-up ms-2"></i></>
                ) : (
                  <>See More Tools <i className="fa-solid fa-chevron-down ms-2"></i></>
                )}
              </button>
            </div>
          )}

        </div>

        <style jsx>{`
          .features-section {
            background-color: #020617; /* Slate 950 base */
            font-family: 'Inter', system-ui, sans-serif;
          }
          
          .bg-absolute {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 0;
          }
          
          .bg-dark-base {
            background-image: 
              linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
            background-size: 60px 60px;
            background-position: center center;
            opacity: 0.5;
            mask-image: radial-gradient(ellipse at center, black 30%, transparent 80%);
            -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 80%);
          }

          .ambient-glow {
            position: absolute;
            border-radius: 50%;
            filter: blur(120px);
            z-index: 0;
            opacity: 0.3;
            pointer-events: none;
            animation: float-glow 25s ease-in-out infinite alternate;
          }

          .glow-primary {
            width: 600px;
            height: 600px;
            background: rgba(99, 102, 241, 0.15); /* Indigo */
            top: 20%;
            right: -100px;
            animation-delay: -5s;
          }

          .glow-secondary {
            width: 500px;
            height: 500px;
            background: rgba(139, 92, 246, 0.15); /* Violet */
            bottom: -100px;
            left: -100px;
            animation-delay: -15s;
          }

          @keyframes float-glow {
            0% { transform: translate(0, 0) scale(1); }
            33% { transform: translate(30px, -50px) scale(1.1); }
            66% { transform: translate(-20px, 20px) scale(0.9); }
            100% { transform: translate(0, 0) scale(1); }
          }

          .spin-slow {
            animation: spin 30s linear infinite;
          }
          
          .spin-slow-reverse {
            animation: spin 20s linear infinite reverse;
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          .glass-panel {
            background: rgba(15, 23, 42, 0.4);
            backdrop-filter: blur(16px);
            -webkit-backdrop-filter: blur(16px);
          }

          .border-white-10 {
            border-color: rgba(255, 255, 255, 0.08) !important;
          }

          .text-secondary {
            color: #94a3b8 !important; /* Slate 400 */
          }
          
          .text-xs {
            font-size: 0.75rem;
          }

          .tracking-wider {
            letter-spacing: 0.05em;
          }

          .badge-premium {
            background: linear-gradient(180deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.7) 100%);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1);
          }

          .badge-dot {
            width: 8px;
            height: 8px;
            background-color: #8b5cf6; /* Violet 500 */
            border-radius: 50%;
            box-shadow: 0 0 8px #8b5cf6;
            animation: pulse-dot 2s infinite;
          }

          @keyframes pulse-dot {
            0% { box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4); }
            70% { box-shadow: 0 0 0 6px rgba(139, 92, 246, 0); }
            100% { box-shadow: 0 0 0 0 rgba(139, 92, 246, 0); }
          }

          .title-glow {
            text-shadow: 0 0 40px rgba(255, 255, 255, 0.1);
          }

          .text-gradient {
            background: linear-gradient(135deg, #818cf8, #c084fc);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          /* Feature Cards */
          .ai-feature-card {
            transition: transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.5s ease;
          }

          .shadow-premium {
            box-shadow: 
              0 10px 30px -10px rgba(0, 0, 0, 0.5),
              0 0 0 1px rgba(255, 255, 255, 0.02);
          }

          .ai-feature-card:hover {
            transform: translateY(-8px);
            box-shadow: 
              0 25px 50px -12px rgba(0, 0, 0, 0.8),
              0 0 0 1px rgba(255, 255, 255, 0.1),
              0 0 40px rgba(99, 102, 241, 0.15);
          }

          .ai-feature-card:hover .ai-card-glow {
            opacity: 1 !important;
          }

          .ai-feature-card:hover .glass-panel {
            border-color: rgba(255, 255, 255, 0.15) !important;
          }

          .icon-wrapper {
            width: 56px;
            height: 56px;
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.08);
            box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.1);
            transition: transform 0.4s ease, background 0.4s ease, border-color 0.4s ease;
          }

          .ai-feature-card:hover .icon-wrapper {
            transform: scale(1.1) rotate(5deg);
            background: rgba(99, 102, 241, 0.15);
            border-color: rgba(99, 102, 241, 0.4);
          }

          .ai-link i {
            transition: transform 0.3s ease, opacity 0.3s ease;
          }

          .ai-feature-card:hover .ai-link i {
            transform: translateX(6px);
            opacity: 1 !important;
          }
          
          .pointer-events-none {
            pointer-events: none;
          }
          
          .transition-opacity {
            transition: opacity 0.4s ease;
          }
        `}</style>
      </section>
    </>
  );
};

export default FeaturesSection;